import { _ } from "../../core/polyglot/polyglot.js";
import { eventManager } from "@foretagsplatsen/yaem";
import {
	makeComputationApiRepository,
	makeMonitorRepository,
} from "../../core/repositories/repositoryBuilders.js";
import accountingDocument from "../shared/accountingDocument.js";
import Budget from "../../models/Budget.js";
import BudgetEditorActionsWidget from "./BudgetEditorActionsWidget.js";
import BudgetEditorWidget from "./budgetEditor/BudgetEditorWidget/BudgetEditorWidget.js";
import BudgetReport from "../../models/BudgetReport.js";
import Forecast from "../../models/forecast/instance/Forecast.js";
import optionsBag from "../../models/options/optionsBag.js";
import ReportService from "../reports2/ReportService.js";
import session from "../../core/session.js";

/**
 * Document that renders an editor for a budget using the report rendering logic.
 */
const budgetEditorDocument = accountingDocument.subclass((that, my) => {
	my.initialize = function () {
		my.super(...arguments);

		my.budgetRepository = makeMonitorRepository({
			route: "Accounting2/Company/#companyId/Budget/#budgetId",
			model: Budget,
			defaultFilters: {
				companyId: session.getCompany().getLegacyId(),
				budgetId: "",
			},
			antiForgeryToken: session.getAntiForgeryToken(),
		});

		my.forecastRepository = makeComputationApiRepository({
			route: "/Forecasts",
			session,
			model: Forecast,
		});

		my.forecastAsBudgetRepository = makeMonitorRepository({
			route: "/Accounting2/Company/#companyId/Forecast/#forecastId/Budget/#budgetId",
			model: Budget,
			defaultFilters: { companyId: session.getCompany().getLegacyId() },
			antiForgeryToken: session.getAntiForgeryToken(),
		});

		my.budgetReport = new BudgetReport();

		that.title = (html) => {
			const name = my.getBudgetReport().getName();
			html.render(name || _("Budget editor"));
		};

		that.subtitle = (html) => {
			if (!my.getBudget()) {
				return;
			}

			html.render(
				_("This is our plan for {period}", {
					period: my.getBudget().periodName(),
				}),
			);
		};

		that.showEmpty();
	};

	my.initializeSubwidgets = function () {
		my.budgetEditor = new BudgetEditorWidget({
			budgetReport: my.getBudgetReport(),
			onBudgetChanged: my.onBudgetChanged,
			fiscalYears: session.getCompany().getActiveYears(),
			onAccountStatementRedirectionRequest: my.redirectToAccountStatement,
		});

		my.budgetEditorActionsWidget = new BudgetEditorActionsWidget({
			budgetReport: my.getBudgetReport(),
			displaySettingsUpdatedCallback: () => my.updateBudgetReport(),
			dimensionChangedCallback: () => my.updateBudgetReport(),
			accountRowAddedCallback: (accountNumber) => {
				my.getBudgetReport().addAlwaysVisibleAccountNumber(
					accountNumber,
				);

				that.update();
			},
			onImportDataCallback: () => {
				my.onBudgetChanged(my.updateBudgetReport);
				that.update();
			},
			onAddIncomeAndCostAccountsCallback: () => {
				my.addBudgetRowsForIncomeAndCostAccounts();
				that.update();
			},
			onSaveBudgetFromForecastId: my.onSaveBudgetFromForecastId,
			getAllBudgets: my.getAllBudgets,
			getAllForecasts: optionsBag.fromId("forecast-to-budget").isActive()
				? () => my.forecastRepository.get()
				: () => [],
			displayBudgetActions: my.canEditBudget(),
		});
	};

	that.hasPeriodPicker = function () {
		return false;
	};

	that.hasActionsMenu = function () {
		return true;
	};

	that.hasHelp = function () {
		return false;
	};

	that.actions = function () {
		return [my.budgetEditorActionsWidget];
	};

	that.show = function (budgetId) {
		that.showUpdating();

		my.budgetEditorActionsWidget.show();

		my.updateBudgetReport({
			budgetId,
			callback: () => that.showContent(),
		});
	};

	my.renderContentOn = function (html) {
		html.render(my.budgetEditor);
	};

	my.updateBudgetReport = function ({
		budgetId = my.getBudget().getId(),
		callback = () => my.budgetEditor.update(),
	} = {}) {
		my.budgetRepository
			.get({ filter: { budgetId } })
			.then((budget) => {
				my.budgetReport.setBudget(budget);
				return my.updateReport();
			})
			.then(callback);
	};

	my.updateReport = async function () {
		const firstMonth = my.getBudget().getFiscalYear().asMonths().shift();

		my.reportService = new ReportService({
			period: firstMonth,
			reportTemplate: my.budgetReport.getReportTemplate(),
			company: session.getCompany(),
			chartOfAccountsId: session.getCurrentChartOfAccountsId(),
		});

		await my.reportService.fetchChartOfAccountsAndKeyFigures();

		await my.reportService.updateReportAndData({
			dimensionSpecification:
				my.budgetReport.generateDimensionSpecification(),
			itemSettings: my.budgetReport.generateTemplateSettings(),
		});

		my.budgetReport.setReport(my.reportService.getReport());

		my.budgetEditorActionsWidget.updateBudget();
	};

	my.onBudgetChanged = function (callback) {
		my.saveBudget();
		my.computeReportValues(callback);
	};

	my.saveBudget = function () {
		const budget = my.getBudget();

		my.budgetRepository
			.update({
				model: budget,
				filter: { budgetId: budget.getId() },
				notify: false,
				readResponse: false,
			})
			.then(() => my.notifySaveSucceeded())
			.catch(() => my.notifySaveFailed());
	};

	my.computeReportValues = async function (callback) {
		await my.reportService.updateReportData({ throttle: true });

		callback();
	};

	my.notifySaveSucceeded = function () {
		eventManager.at("autoSave").trigger("success");
	};

	my.notifySaveFailed = function () {
		eventManager.at("autoSave").trigger("failure");
	};

	my.getBudget = function () {
		return my.budgetReport.getBudget();
	};

	my.getReport = function () {
		return my.budgetReport.getReport();
	};

	my.getBudgetReport = function () {
		return my.budgetReport;
	};

	my.getAllBudgets = function () {
		return my.budgetRepository.get();
	};

	my.getChartOfAccounts = function () {
		return my.budgetReport.getChartOfAccounts();
	};

	my.redirectToAccountStatement = function ({ accountNumber, period }) {
		window.open(
			my.router.linkTo("account-statement", {
				accountNumber,
				period,
				dimensionSpecification: my.budgetReport
					.generateDimensionSpecification()
					?.toString(),
			}),
		);
	};

	my.addBudgetRowsForIncomeAndCostAccounts = function () {
		my.getIncomeAndCostAccounts()
			.map((account) => account.getNumber())
			.forEach((accountNumber) =>
				my
					.getBudgetReport()
					.addAlwaysVisibleAccountNumber(accountNumber),
			);
	};

	my.onSaveBudgetFromForecastId = function (forecastId) {
		return my.forecastAsBudgetRepository.update({
			filter: {
				budgetId: my.getBudget().getId(),
				forecastId,
			},
		});
	};

	my.getIncomeAndCostAccounts = function () {
		const chartOfAccounts = my.getChartOfAccounts();
		const incomeGroup = chartOfAccounts.findById("Income");
		const costGroup = chartOfAccounts.findById("Costs");
		return my
			.getBudget()
			.getFiscalYear()
			.getAccountsOfGroups({
				accountGroups: [incomeGroup, costGroup],
			});
	};

	my.canEditBudget = function () {
		const currentUser = session.getCurrentUser();
		return currentUser.canEditBudgetFor(session.getCompany());
	};
});

export default budgetEditorDocument;
